import React, { useState } from 'react';
import { Truck, MapPin, Package, Bike, Clock } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { VehicleType } from '@/types';

const BookTransportForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pickupAddress: '',
    dropoffAddress: '',
    cargoWeightKg: '',
    cargoDescription: '',
    vehiclePreference: 'truck' as VehicleType,
    requesterPhone: user?.phone || ''
  });

  const generateTrackingCode = () => {
    return 'SHIP-' + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const trackingCode = generateTrackingCode();
      
      // Create the shipment order in Firestore
      await addDoc(collection(db, 'shipments'), {
        trackingCode,
        requesterId: user?.id || 'anonymous',
        requesterName: user?.name || 'Guest',
        requesterPhone: formData.requesterPhone,
        requesterType: user?.role || 'consumer',
        pickupLocation: { 
          address: formData.pickupAddress, 
          lat: 0, 
          lng: 0 
        }, // TODO: Add geocoding
        dropoffLocation: { 
          address: formData.dropoffAddress, 
          lat: 0, 
          lng: 0 
        },
        cargoWeightKg: Number(formData.cargoWeightKg),
        cargoDescription: formData.cargoDescription,
        vehiclePreference: formData.vehiclePreference,
        status: 'pending',
        trackingUpdates: [{
          timestamp: new Date(),
          note: 'Order received and awaiting Sal\'s dispatch assignment.',
          location: { lat: 0, lng: 0 }
        }],
        paymentStatus: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      alert(`✅ Transport booked successfully!\n\n🔍 Your tracking code: ${trackingCode}\n\nAn email notification has been sent to HQ. You will be contacted shortly.`);
      
      // Reset form
      setFormData({ 
        pickupAddress: '', 
        dropoffAddress: '', 
        cargoWeightKg: '', 
        cargoDescription: '',
        vehiclePreference: 'truck',
        requesterPhone: user?.phone || ''
      });
    } catch (error) {
      console.error("Error booking transport:", error);
      alert("❌ Failed to book transport. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg max-w-2xl mx-auto border border-gray-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 border-b pb-4">
        <div className="bg-blue-100 p-3 rounded-full text-blue-600">
          <Truck size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Book Logistics</h2>
          <p className="text-sm text-gray-500">Fast, reliable delivery powered by our fleet</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Vehicle Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({...formData, vehiclePreference: 'truck'})}
              className={`p-4 border-2 rounded-xl transition-all flex flex-col items-center gap-2 ${
                formData.vehiclePreference === 'truck' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Truck size={32} />
              <div className="font-semibold">Lorry / Truck</div>
              <div className="text-xs text-gray-500">Long distance & heavy cargo</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({...formData, vehiclePreference: 'motorcycle'})}
              className={`p-4 border-2 rounded-xl transition-all flex flex-col items-center gap-2 ${
                formData.vehiclePreference === 'motorcycle' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Bike size={32} />
              <div className="font-semibold">Rider</div>
              <div className="text-xs text-gray-500">Nairobi local deliveries</div>
            </button>
          </div>
        </div>

        {/* Pickup Location */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <MapPin size={16} className="text-gray-400"/> Pickup Location
          </label>
          <input 
            required
            type="text" 
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="E.g., Gikomba Market, Nairobi"
            value={formData.pickupAddress}
            onChange={(e) => setFormData({...formData, pickupAddress: e.target.value})}
          />
        </div>

        {/* Dropoff Location */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <MapPin size={16} className="text-gray-400"/> Dropoff Location
          </label>
          <input 
            required
            type="text" 
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="E.g., Ruaka, Kiambu"
            value={formData.dropoffAddress}
            onChange={(e) => setFormData({...formData, dropoffAddress: e.target.value})}
          />
        </div>

        {/* Cargo Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Package size={16} className="text-gray-400"/> Weight (Kg)
            </label>
            <input 
              required
              type="number" 
              min="1"
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="e.g., 500"
              value={formData.cargoWeightKg}
              onChange={(e) => setFormData({...formData, cargoWeightKg: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
            <input 
              required
              type="tel" 
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="0712345678"
              value={formData.requesterPhone}
              onChange={(e) => setFormData({...formData, requesterPhone: e.target.value})}
            />
          </div>
        </div>

        {/* Cargo Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cargo Description (Optional)
          </label>
          <textarea 
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            rows={3}
            placeholder="Describe what you're shipping (e.g., Onions - Red Large)"
            value={formData.cargoDescription}
            onChange={(e) => setFormData({...formData, cargoDescription: e.target.value})}
          />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl flex items-start gap-3">
          <Clock className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <strong>Processing Time:</strong> Your booking will be reviewed within 30 minutes. 
            You'll receive a tracking code via SMS once assigned to a driver.
          </div>
        </div>

        {/* Submit Button */}
        <button 
          type="submit"
          disabled={loading}
          className="w-full bg-gray-900 hover:bg-black text-white p-4 rounded-xl font-bold transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              Processing...
            </>
          ) : (
            <>
              <Truck size={20} />
              Confirm Booking
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default BookTransportForm;
